import React from "react";
import { useUserId } from "../../../user/hooks/useUserId";
import {
  IWizardConfig,
  SubmitButtonColor,
  WizardMode,
} from "../../../wizard/types";
import { useApplicationQueries } from "../../hooks/useApplicationQueries";
import { parseToFrontEndModel } from "../helpers/parseToFrontEndModel";
import { parseToApplicationBackEndModel } from "../helpers/parseToBackEndModel";
import { applyAsStep1 } from "../create/step1/config";
import { applyAsStep2 } from "../create/step2/config";
import { viewContentConfig } from "../view/viewContentConfig";
import { editContentConfig } from "../edit/editContentConfig";
import { useAuth } from "../../../auth/hooks/useAuth";
import { UserRole } from "../../../user/enums/UserRole";
import { payForApplicationConfig } from "../pay/payConfig";
import useBillingQueries from "../../../billing/hooks/useBillingQueries";
import { ICreateReceiptBackendModel } from "../../../billing/types";
import { INote } from "../../types";
import { NoteContent } from "../note/NoteContent";
import Content from "../cancel/Content";
import validator from "../cancel/validator";
import { parseToLanguageCourseApplicationViewFrontEndModel } from "../helpers/parseToLanguageCourseApplicationViewFrontEndModel";
import { parseToProgramApplicationViewFrontEndModel } from "../helpers/parseToProgramApplicationViewFrontEndModel";
import { EducationType } from "../../../school/constants/types";
import { parseToEditProgramApplicationBackEndModel } from "../helpers/parseToEditProgramApplicationBackEndModel";

async function getApplyAsInitialData({
  getProgram,
  getStudents,
  programId,
  agentId,
  educationType,
  studentId,
}) {
  const { data: students } = !!studentId ? [] : await getStudents(agentId);
  const { data: program } = await getProgram(programId, educationType);

  return parseToFrontEndModel({
    students,
    program,
    programId,
    educationType,
    studentId,
  });
}

async function createApplication(formData, create, educationType) {
  const backEndModel = parseToApplicationBackEndModel(formData);

  const response = await create(backEndModel, educationType);

  return {
    data: {
      message: response.data.message,
      studentId: backEndModel.studentId,
    },
  };
}

async function getApplicationViewData({ getViewData, id, educationType }) {
  const { data } = await getViewData(id, educationType);

  switch (educationType) {
    case EducationType.Language:
      return parseToLanguageCourseApplicationViewFrontEndModel(
        data,
        educationType,
      );
    case EducationType.Secondary:
    case EducationType.Higher:
      return parseToProgramApplicationViewFrontEndModel(data, educationType);
  }
}

async function updateApplicationData({
  data,
  updateApplication,
  educationType,
  id,
}) {
  const backEndModel = parseToEditProgramApplicationBackEndModel({
    ...data,
    id,
  });

  return await updateApplication(backEndModel, educationType, id);
}

async function createReceiptForApplication({
  data,
  createReceipt,
  educationType,
  applicationId,
}) {
  const backEndModel: ICreateReceiptBackendModel = {
    applicationId: applicationId,
    amount: data.amount,
    currency: data.currency.id,
    ...(data.receiptImage?.id && {
      image: {
        id: data.receiptImage.id,
      },
    }),
  };

  return await createReceipt(backEndModel, educationType);
}

const getNoCreditsContent = (hasPackage) =>
  hasPackage
    ? "Your credit limit exceeded, for more applications get a package."
    : "Dear Applicant, to activate Your account get a package.";

export function useApplicationConfig() {
  const {
    getProgram,
    getStudents,
    create,
    checkDuplication,
    getViewData,
    cancelApplication,
    updateApplication,
  } = useApplicationQueries();

  const { createReceipt } = useBillingQueries();
  const agentId = useUserId();
  const { user } = useAuth();
  const studentId = user.role === UserRole.Student ? user.id : null;

  const getCreateApplicationConfig = (
    programId,
    educationType,
  ): IWizardConfig => ({
    steps: [
      { ...applyAsStep1, backendValidator: checkDuplication },
      applyAsStep2,
    ],
    submitButtonText: "Save",
    getInitialData: () =>
      getApplyAsInitialData({
        getProgram,
        getStudents,
        programId,
        agentId,
        educationType,
        studentId,
      }),
    saveCallback: (data) => createApplication(data, create, educationType),
  });

  const getViewApplicationConfig = (id, educationType) => ({
    steps: [viewContentConfig],
    submitButtonText: null,
    cancelButtonText: "Close",
    mode: WizardMode.View,
    getInitialData: () =>
      getApplicationViewData({ getViewData, id, educationType }),
  });

  const getEditApplicationConfig = (id, educationType) => ({
    steps: [editContentConfig],
    submitButtonText: "Save",
    mode: WizardMode.Edit,
    getInitialData: () =>
      getApplicationViewData({ getViewData, id, educationType }),
    saveCallback: (data) =>
      updateApplicationData({ data, updateApplication, educationType, id }),
  });

  const getPayForApplicationConfig = (applicationId, educationType) => ({
    steps: [payForApplicationConfig],
    submitButtonText: "Save",
    mode: WizardMode.Create,
    submitButtonColor: SubmitButtonColor.Success,
    saveCallback: (data) =>
      createReceiptForApplication({
        data,
        createReceipt,
        educationType,
        applicationId,
      }),
  });

  const getNoCreditsConfig = (hasPackage: boolean): IWizardConfig => ({
    steps: [
      {
        titleConfig: {
          titleText: "Application limit exceeded",
        },
        width: "464px",
        content: getNoCreditsContent(hasPackage),
      },
    ],
    submitButtonText: "Go to packages",
    submitButtonColor: SubmitButtonColor.Default,
  });

  const getCancelConfig = (id, educationType): IWizardConfig => ({
    steps: [
      {
        titleConfig: {
          titleText: "Cancel Application",
        },
        width: "552px",
        content: () => <Content />,
        validator: validator,
      },
    ],
    submitButtonText: "Proceed",
    submitButtonColor: SubmitButtonColor.Danger,
    cancelButtonText: "Close",
    saveCallback: (data) =>
      cancelApplication(id, educationType, {
        text: data.reason,
      }),
  });

  const getNoteConfig = (note: INote): IWizardConfig => ({
    steps: [
      {
        titleConfig: {
          titleText: "Note",
        },
        width: "552px",
        content: () => <NoteContent note={note} />,
      },
    ],
    submitButtonText: null,
  });

  return {
    getPayForApplicationConfig,
    getCreateApplicationConfig,
    getViewApplicationConfig,
    getEditApplicationConfig,
    getNoCreditsConfig,
    getCancelConfig,
    getNoteConfig,
  };
}
